import { useEffect, useState, useCallback } from "react";
import "@/App.css";
import axios from "axios";
import {
  Lightning,
  Book,
  Video,
  Robot,
  UploadSimple,
  Globe,
  ShieldCheck,
  MusicNote,
  FilmStrip,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { BookCreation } from "@/components/BookCreation";
import { VideoCreation } from "@/components/VideoCreation";
import { AutonomousAgents } from "@/components/AutonomousAgents";
import { FileUpload } from "@/components/FileUpload";
import { NetlifyBuilder } from "@/components/NetlifyBuilder";
import { MusicCreation } from "@/components/MusicCreation";
import { ReelStudio } from "@/components/ReelStudio";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import LoginPage from "@/pages/LoginPage";
import { UpgradePro } from "@/components/UpgradePro";
import { AdminDashboard } from "@/components/AdminDashboard";
import { SubscriptionManager } from "@/components/SubscriptionManager";
import { TasksPanel } from "@/components/TasksPanel";
import { ChatPane } from "@/components/ChatPane";
import { PaymentReturnHandler } from "@/components/PaymentReturnHandler";

// Configure axios defaults for cookies
axios.defaults.withCredentials = true;

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AppContent() {
  const { user, loading, usage, logout, fetchUsage } = useAuth();
  const [activeTab, setActiveTab] = useState("tasks");
  const [sessionId] = useState(() => `session_${Date.now()}`);
  const [messages, setMessages] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [agentActivity, setAgentActivity] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showSubscription, setShowSubscription] = useState(false);
  const [showNewTask, setShowNewTask] = useState(false);
  const [newTask, setNewTask] = useState({
    title: "", description: "", priority: "medium", due_date: "", assigned_to: "",
  });

  const loadChatHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/chat/history/${sessionId}`);
      setMessages(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error("Error loading chat history:", error);
    }
  }, [sessionId]);

  const loadTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tasks`);
      setTasks(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error("Error loading tasks:", error);
    }
  }, []);

  const loadAgentActivity = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/agent/activity`);
      setAgentActivity(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === "development") console.error("Error loading agent activity:", error);
    }
  }, []);

  useEffect(() => {
    loadChatHistory();
    loadTasks();
    loadAgentActivity();
  }, [loadChatHistory, loadTasks, loadAgentActivity]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || isLoading) return;
    const userMessage = inputMessage;
    setInputMessage("");
    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/chat/send`, { session_id: sessionId, message: userMessage });
      setMessages((prev) => [...prev, { role: "user", content: userMessage, timestamp: new Date().toISOString() }, response.data.message]);
      if (response.data.task_created) {
        toast.success("Task created by AI!");
        loadTasks();
        loadAgentActivity();
      }
      fetchUsage();
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail || "Rate limit reached");
      } else {
        toast.error("Failed to send message");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const createTask = async (e) => {
    e.preventDefault();
    if (!newTask.title.trim()) return;
    try {
      await axios.post(`${API}/tasks`, newTask);
      toast.success("Task created!");
      setNewTask({ title: "", description: "", priority: "medium", due_date: "", assigned_to: "" });
      setShowNewTask(false);
      loadTasks();
      loadAgentActivity();
    } catch (error) {
      toast.error("Failed to create task");
    }
  };

  const updateTaskStatus = async (taskId, newStatus) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, { status: newStatus });
      loadTasks();
      loadAgentActivity();
      toast.success(`Task ${newStatus}!`);
    } catch (error) {
      toast.error("Failed to update task");
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      loadTasks();
      loadAgentActivity();
      toast.success("Task deleted");
    } catch (error) {
      toast.error("Failed to delete task");
    }
  };

  const analyzeTask = async (taskId) => {
    try {
      const response = await axios.post(`${API}/agent/analyze?task_id=${taskId}`);
      toast.success("Task analyzed!");
      toast.info(response.data.analysis.slice(0, 100) + "...");
      loadAgentActivity();
      fetchUsage();
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error(error.response.data.detail || "Rate limit reached");
      } else {
        toast.error("Failed to analyze task");
      }
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "text-red-500 border-red-500";
      case "medium": return "text-yellow-500 border-yellow-500";
      case "low": return "text-green-500 border-green-500";
      default: return "text-gray-500 border-gray-500";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return "text-green-500";
      case "in_progress": return "text-yellow-500";
      case "pending": return "text-gray-400";
      default: return "text-gray-400";
    }
  };

  if (loading) {
    return (
      <div className="dark">
        <div className="h-screen bg-black flex items-center justify-center">
          <div className="text-neutral-400 text-sm">Loading...</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginPage />;
  }

  const isAdmin = user.role === "admin";

  const tabs = [
    { id: "tasks", icon: Lightning, label: "Tasks" },
    { id: "books", icon: Book, label: "Books" },
    { id: "videos", icon: Video, label: "Videos" },
    { id: "agents", icon: Robot, label: "Agents" },
    { id: "files", icon: UploadSimple, label: "Files" },
    { id: "music", icon: MusicNote, label: "Music" },
    { id: "reels", icon: FilmStrip, label: "Reels" },
    { id: "netlify", icon: Globe, label: "Netlify" },
    ...(isAdmin ? [{ id: "admin", icon: ShieldCheck, label: "Admin" }] : []),
  ];

  return (
    <div className="dark">
      <div className="App h-screen flex flex-col bg-black text-white">
        <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
          {/* Left Pane - Chat */}
          <ChatPane
            user={user}
            messages={messages}
            inputMessage={inputMessage}
            setInputMessage={setInputMessage}
            isLoading={isLoading}
            sendMessage={sendMessage}
            logout={logout}
            usage={usage}
            isAdmin={isAdmin}
            onUpgradeClick={user.role !== "pro" ? () => setShowUpgrade(true) : undefined}
            onManageSubscription={() => setShowSubscription(true)}
          />

          {/* Right Pane - Tabbed Interface */}
          <div className="w-full md:w-1/2 h-1/2 md:h-full flex flex-col">
            <div className="border-b border-border bg-surface flex overflow-x-auto" data-testid="tab-navigation">
              {tabs.map(({ id, icon: Icon, label }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id)}
                  className={`flex-1 p-4 flex items-center justify-center gap-2 transition-all ${activeTab === id ? "bg-black border-b-2 border-white" : "hover:bg-accent"}`}
                  data-testid={`${id}-tab-button`}
                >
                  <Icon size={20} weight={activeTab === id ? "fill" : "regular"} />
                  <span className="font-medium text-sm">{label}</span>
                </button>
              ))}
            </div>
            <div className="flex-1 overflow-hidden">
              {activeTab === "tasks" && (
                <TasksPanel
                  tasks={tasks} showNewTask={showNewTask} setShowNewTask={setShowNewTask}
                  newTask={newTask} setNewTask={setNewTask} createTask={createTask}
                  updateTaskStatus={updateTaskStatus} deleteTask={deleteTask}
                  analyzeTask={analyzeTask} getPriorityColor={getPriorityColor}
                  getStatusColor={getStatusColor} agentActivity={agentActivity}
                />
              )}
              {activeTab === "books" && <BookCreation />}
              {activeTab === "videos" && <VideoCreation />}
              {activeTab === "agents" && <AutonomousAgents />}
              {activeTab === "files" && <FileUpload />}
              {activeTab === "music" && <MusicCreation />}
              {activeTab === "reels" && <ReelStudio />}
              {activeTab === "netlify" && <NetlifyBuilder />}
              {activeTab === "admin" && isAdmin && <AdminDashboard />}
            </div>
          </div>
        </div>
      </div>
      {showUpgrade && <UpgradePro onClose={() => setShowUpgrade(false)} />}
      {showSubscription && <SubscriptionManager onClose={() => setShowSubscription(false)} />}
      <PaymentReturnHandler />
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
